import React from 'react';
import { Button } from '../components/ui/button';
import { Building2, Users, TrendingUp, Shield, Clock, Award } from 'lucide-react';

const BusinessPage = () => {
  const features = [
    {
      icon: Building2,
      title: 'Bedriftsavtaler',
      description: 'Skreddersydde løsninger for bedrifter som ønsker å selge flere biler'
    },
    {
      icon: Users,
      title: 'Dedikert kontaktperson',
      description: 'Få en fast kontaktperson som kjenner din bedrifts behov'
    },
    {
      icon: TrendingUp,
      title: 'Best mulig priser',
      description: 'Over 2000 forhandlere konkurrerer om å kjøpe dine biler'
    },
    {
      icon: Shield,
      title: 'Trygt og sikkert',
      description: 'Full kontroll og dokumentasjon på alle transaksjoner'
    },
    {
      icon: Clock,
      title: 'Rask prosess',
      description: 'Effektiv håndtering av flere biler samtidig'
    },
    {
      icon: Award,
      title: 'Over 10 års erfaring',
      description: 'Vi har hjulpet hundrevis av bedrifter med bilsalg'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                DigiCar for bedrifter
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Selg bedriftsbiler raskt og enkelt. Vi tilbyr skreddersydde løsninger for bedrifter som ønsker å selge en eller flere biler.
              </p>
              <Button className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold text-lg px-8 py-6">
                Kontakt oss
              </Button>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1740744362373-bbd648d38dac?w=800&h=600&fit=crop" 
                alt="Bedriftsbiler" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Hvorfor velge DigiCar for bedrifter?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Vi forstår at bedrifter har spesielle behov når det kommer til bilsalg. Våre løsninger er tilpasset nettopp dette.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index} 
                  className="bg-gray-50 rounded-2xl p-8 hover:bg-yellow-50 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="w-14 h-14 bg-yellow-500 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-black" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Slik fungerer det
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: 1,
                title: 'Kontakt oss',
                description: 'Ta kontakt så setter vi opp en bedriftsavtale tilpasset deres behov'
              },
              {
                step: 2,
                title: 'Send inn biler',
                description: 'Registrer biler enkelt gjennom vår bedriftsportal eller send liste til deres kontaktperson'
              },
              {
                step: 3,
                title: 'Vi håndterer resten',
                description: 'Vi tester, fotograferer og legger ut bilene på budrunde. Dere får oversikt og godkjenner bud'
              }
            ].map((item) => (
              <div key={item.step} className="relative">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-yellow-500 text-black rounded-full flex items-center justify-center text-xl font-bold shadow-lg z-10">
                  {item.step}
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-md">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-yellow-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Klar til å komme i gang?
          </h2>
          <p className="text-xl text-gray-900 mb-8">
            Kontakt oss i dag for en uforpliktende samtale om hvordan vi kan hjelpe din bedrift
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-black hover:bg-gray-800 text-white font-semibold text-lg px-8 py-6">
              Ring oss: 47 49 49 00
            </Button>
            <Button variant="outline" className="bg-white hover:bg-gray-100 text-black border-black font-semibold text-lg px-8 py-6">
              Send e-post
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessPage;